import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-28 h-20 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span>{article.viewCount.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-center gap-3 py-2.5 cursor-pointer"
      >
        <span className="w-6 h-6 flex items-center justify-center rounded bg-primary-100 text-primary-600 text-xs font-bold flex-shrink-0">
          <i className="ri-fire-line"></i>
        </span>
        <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1 flex-1">
          {article.title}
        </span>
        <span className="text-xs text-foreground-500 flex-shrink-0">
          {article.viewCount.toLocaleString()}
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {article.isHot && (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-600 whitespace-nowrap">
              热门
            </span>
          )}
          <span className="text-xs text-foreground-500">{article.publishDate}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200/60">
          <span className="text-xs text-foreground-500 flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.viewCount.toLocaleString()}
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {article.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs text-secondary-600 bg-secondary-50 px-2 py-0.5 rounded whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}